import { useEffect, useRef, useState } from "react";

let googleInitialized = false;
let activeCredentialHandler = null;

const GoogleSignInButton = ({
  onCredential,
  disabled = false,
  onLoadError,
}) => {
  const buttonRef = useRef(null);
  const [ready, setReady] = useState(false);

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    activeCredentialHandler = onCredential;

    return () => {
      if (activeCredentialHandler === onCredential) {
        activeCredentialHandler = null;
      }
    };
  }, [onCredential]);

  useEffect(() => {
    if (!clientId) {
      setReady(false);
      onLoadError?.(
        new Error("REACT_APP_GOOGLE_CLIENT_ID is not configured.")
      );
      return undefined;
    }

    let cancelled = false;
    let loadListenerAttached = false;

    const renderButton = () => {
      if (
        cancelled ||
        !window.google?.accounts?.id ||
        !buttonRef.current
      ) {
        return;
      }

      if (!googleInitialized) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (credentialResponse) => {
            activeCredentialHandler?.(credentialResponse);
          },
          ux_mode: "popup",
          auto_select: false,
        });

        googleInitialized = true;
      }

      const width = Math.max(
        240,
        Math.min(400, buttonRef.current.clientWidth || 400)
      );

      buttonRef.current.innerHTML = "";

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        logo_alignment: "left",
        width,
      });

      if (!cancelled) {
        setReady(true);
      }
    };

    if (window.google?.accounts?.id) {
      renderButton();
    } else {
      const script = document.getElementById("google-identity-services");

      if (!script) {
        onLoadError?.(
          new Error(
            "Google Identity Services script is missing from public/index.html."
          )
        );
        return undefined;
      }

      const handleLoad = () => {
        renderButton();
      };

      const handleError = () => {
        if (!cancelled) {
          setReady(false);
          onLoadError?.(
            new Error("Google Identity Services failed to load.")
          );
        }
      };

      script.addEventListener("load", handleLoad, { once: true });
      script.addEventListener("error", handleError, { once: true });
      loadListenerAttached = true;

      return () => {
        cancelled = true;

        if (loadListenerAttached) {
          script.removeEventListener("load", handleLoad);
          script.removeEventListener("error", handleError);
        }
      };
    }

    return () => {
      cancelled = true;
    };
  }, [clientId, onLoadError]);

  return (
    <div
      className={`w-full overflow-hidden ${
        disabled ? "pointer-events-none opacity-50" : ""
      }`}
    >
      <div
        ref={buttonRef}
        className="flex min-h-[40px] w-full justify-center"
      />

      {!ready && clientId && (
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Loading Google sign-in...
        </p>
      )}
    </div>
  );
};

export default GoogleSignInButton;